import * as Yup from 'yup';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Unstable_Grid2';
import { paths } from '@/routes/paths';
import { useRouter } from '@/routes/hooks';
import FormProvider, { RHFTextField, RHFGoogleAddressField } from '@/components/forms/hook-form';
import { Box, Stack, Button, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '@/utils/api/axios';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/components/feedback/snackbar';
import { ShowIf } from '@/utils';
import { validateTaxId, getTaxIdErrorMessage } from '@/utils/validation/tax-validators';
import {
  DEFAULT_FORM_VALUES,
  ILegalEntityFormProps,
  ILegalEntityFormValues,
} from '@/types/legal-entity-form';
import { useAddressValidationSchema } from '@/sections/management/accounts/orders/hooks/use-address-validation-schema';

const REDIRECT_PATH = (organizationId: string) =>
  paths.management.accounts.organizations.tab(organizationId, 'legal-entities');

export function LegalEntityNewEditForm({
  currentLegalEntity,
  organization,
  countries,
  onSuccess,
  onCancel,
}: ILegalEntityFormProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const baseLegalEntitySchema: Yup.ObjectSchema<ILegalEntityFormValues> = Yup.object({
    legal_name: Yup.string().required(t('Name is required')),
    tax_number: Yup.string()
      .test('validTaxId', (value, context) => {
        if (!selectedCountry || !value) return true;
        const isValid = validateTaxId(selectedCountry, value);
        return (
          isValid ||
          context.createError({
            message: `${selectedCountry}: ${getTaxIdErrorMessage(selectedCountry)}`,
          })
        );
      })
      .required(t('Tax number is required')),
    erp_id: Yup.string().optional(),
    activity: Yup.string().optional(),
    address: useAddressValidationSchema().addressValidationSchema,
    dataGooglePlaces: Yup.object().optional(),
    selectedCountry: Yup.string().optional(),
    country: Yup.string().optional(),
    city: Yup.string().optional(),
  });

  const NewLegalEntitySchema = baseLegalEntitySchema;
  const EditLegalEntitySchema = baseLegalEntitySchema;
  const defaultValues = useMemo(
    () => ({
      ...DEFAULT_FORM_VALUES,
      ...currentLegalEntity,
      selectedCountry:
        countries.find((country) => country._id === currentLegalEntity?.country)?.name || '',
      dataGooglePlaces: currentLegalEntity?.dataGooglePlaces || undefined,
    }),
    [currentLegalEntity, countries]
  );

  const methods = useForm<ILegalEntityFormValues>({
    resolver: yupResolver(currentLegalEntity ? EditLegalEntitySchema : NewLegalEntitySchema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const [selectedCountry, setSelectedCountry] = useState<string>(
    defaultValues?.selectedCountry || ''
  );

  const { mutate } = useMutation({
    mutationFn: async (data: any) => {
      data.organization = organization?._id;
      if (data.country === '') data.country = null;
      if (currentLegalEntity) {
        return axios.patch(`legal-entities/${currentLegalEntity._id}`, data);
      }
      return axios.post(`legal-entities`, data);
    },
    onSuccess: async (data) => {
      if (currentLegalEntity) {
        await queryClient.invalidateQueries({
          queryKey: [`legalEntities${currentLegalEntity._id}edit`],
        });
      }

      queryClient.refetchQueries({
        queryKey: ['legalEntities', organization?._id?.toString()],
      });

      enqueueSnackbar(currentLegalEntity ? t('Update success!') : t('Create success!'));
      reset();

      if (onSuccess) {
        onSuccess(data.data);
      } else {
        router.push(REDIRECT_PATH(organization?._id || ''));
      }
    },
    onError: (error) => {
      enqueueSnackbar(t('An error occurred while saving the legal entity'), { variant: 'error' });
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      mutate(data);
    } catch (error) {
      // Error handled by mutation onError
    }
  });

  const isOldLegalEntity = !!(currentLegalEntity?.country || currentLegalEntity?.city);

  // Memoize country display name
  const currentCountryName = useMemo(() => {
    if (!currentLegalEntity?.country) return '';
    return countries.find((country: any) => country._id === currentLegalEntity.country)?.name || '';
  }, [countries, currentLegalEntity?.country]);

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Grid container spacing={3}>
        <Grid xs={12} md={12}>
          <Card sx={{ overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {/* BASIC LEGAL ENTITY INFORMATION SECTION */}
              <Box sx={{ p: 2.5, backgroundColor: 'background.paper' }}>
                <Typography
                  variant="h6"
                  color="text.primary"
                  sx={{ mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}
                >
                  {currentLegalEntity ? t('Edit Legal Entity') : t('Create Legal Entity')}
                </Typography>

                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.95rem' }}
                >
                  {t('Physical Address')}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <RHFGoogleAddressField
                    name="address"
                    label={t('Address')}
                    placeholder={t('Search and select the complete address using Google Places')}
                    autoComplete="off"
                    onPlaceSelected={(place) => {
                      setValue('dataGooglePlaces', place);
                      setValue('address', place.formatted_address?.toString() || '');
                      const countryComponent = place.address_components?.find((component) =>
                        component.types.includes('country')
                      );
                      const countryName =
                        countryComponent?.short_name === 'US'
                          ? 'USA'
                          : countryComponent?.long_name || '';

                      setSelectedCountry(countryName);

                      const country = countries.find(
                        (country) => country.name.toLowerCase() === countryName.toLowerCase()
                      );

                      setValue('country', country?._id || '');
                    }}
                    fullWidth
                  />
                </Box>

                {/* Basic Legal Information */}
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.95rem' }}
                >
                  {t('Basic Legal Information')}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <RHFTextField
                    name="legal_name"
                    label={t('Legal Name')}
                    placeholder={t('Enter the complete legal name of the entity')}
                    fullWidth
                  />
                </Box>

                <Box
                  rowGap={2}
                  columnGap={2}
                  display="grid"
                  sx={{ mb: 2 }}
                  gridTemplateColumns={{
                    xs: 'repeat(1, 1fr)',
                    sm: 'repeat(2, 1fr)',
                  }}
                >
                  <RHFTextField
                    name="tax_number"
                    label={t('Tax Number')}
                    placeholder={t('Enter the tax identification number')}
                    disabled={!selectedCountry}
                    helperText={
                      !selectedCountry
                        ? t('Please select an address first to enable tax number input')
                        : t(
                            'The Tax Identifier validation is performed according to the country of the address specified in this form.'
                          )
                    }
                  />
                  <RHFTextField
                    name="erp_id"
                    label={t('ERP ID')}
                    placeholder={t('Enter the ERP system identifier (optional)')}
                  />
                </Box>

                <Box sx={{ mb: 0 }}>
                  <RHFTextField
                    name="activity"
                    label={t('Business Activity')}
                    placeholder={t('Describe the main business activity or sector')}
                    fullWidth
                  />
                </Box>
              </Box>

              {/* LEGACY INFORMATION SECTION - Only for existing legal entities with old data */}
              <ShowIf condition={isOldLegalEntity}>
                <Box
                  sx={{
                    p: 2.5,
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="text.primary"
                    sx={{ mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}
                  >
                    {t('Legacy Information')}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 1.5, fontWeight: 600, fontSize: '0.95rem' }}
                  >
                    {t('Historical Data (Read-only)')}
                  </Typography>

                  <Box
                    rowGap={2}
                    columnGap={2}
                    display="grid"
                    sx={{ mb: 0 }}
                    gridTemplateColumns={{
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    }}
                  >
                    <ShowIf condition={!!currentLegalEntity?.city}>
                      <RHFTextField
                        name="city"
                        label={t('City')}
                        disabled
                        helperText={t('Legacy city data - use address field for updates')}
                      />
                    </ShowIf>
                    <ShowIf condition={!!currentLegalEntity?.country}>
                      <RHFTextField
                        name="country"
                        label={t('Country')}
                        disabled
                        value={currentCountryName}
                        helperText={t('Legacy country data - use address field for updates')}
                      />
                    </ShowIf>
                  </Box>
                </Box>
              </ShowIf>

              {/* ACTION BUTTONS SECTION */}
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: 'background.neutral',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={
                      onCancel
                        ? onCancel
                        : () =>
                            router.push(
                              paths.management.accounts.organizations.tab(
                                `${organization?._id}`,
                                'legal-entities'
                              )
                            )
                    }
                    sx={{ minWidth: 120 }}
                  >
                    {t('Cancel')}
                  </Button>
                  <LoadingButton
                    color="primary"
                    type="submit"
                    variant="contained"
                    loading={isSubmitting}
                    sx={{ minWidth: 120 }}
                  >
                    {!currentLegalEntity ? t('Create Legal Entity') : t('Save Changes')}
                  </LoadingButton>
                </Stack>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
